import React from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
} from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { addCommas } from '../../utils/functions/commas';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import { useState } from 'react';
import { swalErrorBtn, swalLoading, swalSuccessBtn } from '../../helpers/swalHelper';
import InvoiceListApi from '../../service/invoice.api';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { locale } from 'dayjs';
import 'dayjs/locale/th';

const ariaLabel = { 'aria-label': 'description' };

function TableUploadDefault({ allowEdit, showData, statusETax }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const total = showData?.length || 0;

    const [isSending, setIsSending] = useState(false);

    const DATE_FORMAT = 'DD/MM/YYYY';

    const schema = () => {
        const baseSchema = {
          rows: yup.array().of(
            yup.object().shape({
              invoice_no: yup.string().required('กรุณากรอกเลขที่ใบแจ้งหนี้'),
              taxId: statusETax
                ? yup
                  .string()
                  .required('กรุณากรอกเลขประจำตัว 13 หลัก')
                  .matches(/^[0-9]{13}$/, 'กรุณากรอกเลข 13 หลักเท่านั้น')
                : yup.string(),
              // .required('กรุณากรอกเลขประจำตัว 13 หลัก')
              // .matches(/^[0-9]{13}$/, 'กรุณากรอกเลข 13 หลักเท่านั้น'),
              so_name: yup.string().required('กรุณากรอกชื่อผู้ใช้บริการ'),
              address: statusETax
                ? yup.string().required('กรุณากรอกที่อยู่')
                : yup.string(), // ถ้าไม่มี statusETax จะไม่จำเป็นต้องกรอกที่อยู่
              zip_code: statusETax
                ? yup
                  .string()
                  .required('กรุณากรอกรหัสไปรษณีย์')
                  .length(5, 'กรุณากรอกรหัสไปรษณีย์ให้ครบ 5 หลัก')
                : yup.string(), // ถ้าไม่มี statusETax จะไม่จำเป็นต้องกรอกรหัสไปรษณีย์
              email: yup
                .string()
                .nullable()
                .test(
                  'is-valid-email-list',
                  'กรุณากรอกอีเมลในรูปแบบที่ถูกต้อง (สามารถกรอกหลายอีเมลได้โดยใช้จุลภาคคั่น)',
                  (value) => {
                    // If etaxStatus is true, email is required
                    if (statusETax && !value) {
                      return false; // Email is required when etaxStatus is true
                    }
    
                    if (value) {
                      const emails = value.split(',').map((email) => email.trim());
                      const emailRegex =
                        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                      return emails.every((email) => emailRegex.test(email));
                    }

                    return true; // If no email is provided, it's valid when etaxStatus is false
                  },
                ),
              descriptions: yup.string().required('กรุณากรอกรายละเอียด'),
              amount: yup
                .number()
                .transform((value, originalValue) => {
                  return originalValue.trim() === ''
                    ? null
                    : parseFloat(originalValue.replace(/,/g, ''));
                })
                .nullable()
                .required('กรุณากรอกจำนวนเงิน')
                .test(
                  'is-decimal',
                  'กรุณากรอกจำนวนเงินที่เป็นทศนิยมไม่เกิน 2 ตำแหน่ง',
                  (value) => {
                    return /^(\d+(\.\d{1,2})?)?$/.test(value);
                  },
                ),
            }),
          ),
        };

        return yup.object().shape(baseSchema);
      };

      const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        trigger,
        control,
      } = useForm({
        resolver: yupResolver(schema()),
      });
    
      const { fields, append, remove } = useFieldArray({
        control,
        name: 'rows',
      });
    
      const onSubmit = async (item) => {
        // console.log('item', item);
    
        if (isSending) return;
        setIsSending(true);
    
        swalLoading({title: 'กำลังอัปโหลดข้อมูล...'});
    
        try {
          const updatedData = item.rows.map((row) => ({
            ...row,
            inv_start_date: dayjs(row.inv_start_date, DATE_FORMAT).format(DATE_FORMAT),
            inv_due_date: dayjs(row.inv_due_date, DATE_FORMAT).format(DATE_FORMAT),
          }));
    
          const data = await InvoiceListApi.uploadInvoiceApi(updatedData);
          
          if (data.status_message === 'create success') {
            // setOpenDialog(true);
            swalSuccessBtn({
              title: 'อัปโหลดข้อมูลสำเร็จ'
            }).then((result) => {
              window.location.reload();
            });
          }
        } catch (error) {
          console.log(error);
          swalErrorBtn({title: 'อัปโหลดข้อมูลไม่สำเร็จ'});
        } finally {
          setIsSending(false);
        }
      };
    
      const cancel = () => {
        swalErrorBtn({
          title: 'ยกเลิกการอัปโหลดข้อมูล', 
          icon: 'warning'
        }).then((result) => {
          window.location.reload();
        });
      };
    
      React.useEffect(() => {
        if (showData) {
          // Reset fields before appending new ones
          remove(); // Remove all current rows in the form
          showData.forEach((row) => {
            append({ ...row }); // Append new rows
          });
        }
      }, [showData, append, remove, setValue]);

  return (
    <div>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div style={{ width: '100%', overflowX: 'auto', maxWidth: '90vw', }}>
                <TableContainer component={Paper} style={{ minWidth: 650, maxWidth: '100%', }}>
                    <Table
                      sx={{ minWidth: 650 }}
                      aria-label="simple table"
                      >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ minWidth: 120 }}>เลขที่ใบแจ้งหนี้</TableCell>
                                { statusETax && <TableCell align="center" sx={{ minWidth: 120 }}>เลขประจำตัวผู้เสีบภาษี</TableCell>}
                                <TableCell align="center" sx={{ minWidth: 120 }}>ชื่อลูกค้า</TableCell>
                                { statusETax && <TableCell align="center" sx={{ minWidth: 150 }}>ที่อยู่ลูกค้า</TableCell>}
                                { statusETax && <TableCell align="center" sx={{ minWidth: 120 }}>รหัสไปรษณีย์</TableCell>}
                                <TableCell align="center" sx={{ minWidth: 120 }}>วันที่ใบแจ้งหนี้</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>วันครบกำหนด</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>รายการสินค้า</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ยอดชำระ</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>อีเมลล์</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ลบรายการ</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                          {fields.slice(page * rowsPerPage,Math.min(fields.length, (page + 1) * rowsPerPage),).map((row, index) => {
                            const globalIndex = page * rowsPerPage + index;
                            return (
                            <TableRow hover key={row.no || globalIndex}>
                                {/* เลขที่ใบแจ้งหนี้ */}
                                {allowEdit ? (
                                    <TableCell align='center'>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.invoice_no`)}
                                        defaultValue={row.invoice_no}
                                        inputProps={{style: { textAlign: 'center' },}}
                                        />
                                    </TableCell>
                                ) : (
                                    <TableCell align='center'>
                                      {row.invoice_no || '-'}
                                    </TableCell>
                                )}
                                {/* เลขที่ เลขประจำตัวผู้เสีบภาษี */}
                                { statusETax && (allowEdit ? (
                                    <TableCell>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.taxId`)}
                                        defaultValue={row.taxId}
                                        inputProps={{
                                            style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.taxId}
                                        helperText={
                                            errors?.rows?.[globalIndex]?.taxId?.message
                                        }
                                        />
                                    </TableCell>
                                  ) : (
                                      <TableCell>
                                          {row.taxId || '-'}
                                      </TableCell>
                                  ))
                                }
                                {/* ชื่อลูกค้า */}
                                {allowEdit ? (
                                    <TableCell align="center">
                                      <TextareaAutosize
                                        minRows={1}
                                        defaultValue={row.so_name}
                                        {...register(`rows.${globalIndex}.so_name`)}
                                        aria-label={ariaLabel['aria-label']}
                                        style={{
                                          textAlign: 'left',
                                          width: '120px',
                                          resize: 'none',
                                          border: 'none',
                                          borderBottom: !!errors?.rows?.[globalIndex]?.so_name
                                            ? '1px solid red'
                                            : '1px solid #AFAFAF',
                                          paddingBottom: '4px',
                                          outline: 'none',
                                          background: 'none',
                                        }}
                                      />
                                      {!!errors?.rows?.[globalIndex]?.so_name && (
                                        <p
                                          style={{
                                            color: 'red',
                                            fontSize: '12px',
                                            marginTop: '4px',
                                          }}
                                        >
                                          {errors?.rows?.[globalIndex]?.so_name?.message}
                                        </p>
                                      )}
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                        {row.so_name || '-'}
                                    </TableCell>
                                )}
                                {/* ที่อยู่ลูกค้า */}
                                { statusETax && (allowEdit ? (
                                    <TableCell align="center">
                                      <TextareaAutosize
                                        minRows={1}
                                        defaultValue={row.address}
                                        {...register(`rows.${globalIndex}.address`)}
                                        aria-label={ariaLabel['aria-label']}
                                        style={{
                                          textAlign: 'left',
                                          width: '120px',
                                          resize: 'none',
                                          border: 'none',
                                          borderBottom: !!errors?.rows?.[globalIndex]?.address
                                            ? '1px solid red'
                                            : '1px solid #AFAFAF',
                                          paddingBottom: '4px',
                                          outline: 'none',
                                          background: 'none',
                                        }}
                                      />
                                      {!!errors?.rows?.[globalIndex]?.address && (
                                        <p
                                          style={{
                                            color: 'red',
                                            fontSize: '12px',
                                            marginTop: '4px',
                                          }}
                                        >
                                          {errors?.rows?.[globalIndex]?.address?.message}
                                        </p>
                                      )}
                                    </TableCell>
                                  ) : (
                                      <TableCell align="center">
                                          {row.address || '-'}
                                      </TableCell>
                                  ))
                                }

                                {/* รหัสไปรษณีย์ */}
                                {statusETax && (allowEdit ? (
                                    <TableCell>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.zip_code`)}
                                        defaultValue={row.zip_code}
                                        inputProps={{
                                            style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.zip_code}
                                        helperText={
                                            errors?.rows?.[globalIndex]?.zip_code?.message
                                        }
                                        />
                                    </TableCell>
                                  ) : (
                                      <TableCell align="center">
                                          {row.zip_code || '-'}
                                      </TableCell>
                                  ))
                                }
                                {/* วันที่ใบแจ้งหนี้ */}
                                {allowEdit ? (
                                <TableCell align="center">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                        name={`rows.${globalIndex}.inv_start_date`}
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                            value={
                                                field.value ? dayjs(field.value, DATE_FORMAT) : null
                                            }
                                            onChange={(newValue) => {
                                                field.onChange(
                                                newValue
                                                    ? newValue
                                                    : null,
                                                );
                                            }}
                                            inputFormat={DATE_FORMAT}
                                            renderInput={(params) => (
                                                <TextField
                                                {...params}
                                                variant="standard"
                                                error={
                                                    !!errors?.rows?.[globalIndex]
                                                    ?.inv_start_date
                                                }
                                                helperText={
                                                    errors?.rows?.[globalIndex]
                                                    ?.inv_start_date?.message
                                                }
                                                />
                                            )}
                                            />
                                        )}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                ) : (
                                <TableCell align="center" >
                                    {row.inv_start_date || '-'}
                                </TableCell>
                                )}

                                {/* วันครบกำหนดชำระเงิน */}
                                {allowEdit ? (
                                <TableCell align="center" >
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                        name={`rows.${globalIndex}.inv_due_date`}
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                            value={
                                                field.value ? dayjs(field.value, DATE_FORMAT) : null
                                            }
                                            onChange={(newValue) => {
                                                field.onChange(
                                                newValue
                                                    ? newValue
                                                    : null,
                                                );
                                            }}
                                            inputFormat={DATE_FORMAT}
                                            renderInput={(params) => (
                                                <TextField
                                                {...params}
                                                variant="standard"
                                                error={
                                                    !!errors?.rows?.[globalIndex]
                                                    ?.inv_due_date
                                                }
                                                helperText={
                                                    errors?.rows?.[globalIndex]
                                                    ?.inv_due_date?.message
                                                }
                                                />
                                            )}
                                            />
                                        )}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                ) : (
                                <TableCell align="center">
                                    {row.inv_due_date || '-'}
                                </TableCell>
                                )}

                                {/* รายละเอียด สินค้า บริการ */}
                                {allowEdit ? (
                                <TableCell align="center">
                                    <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.descriptions`)}
                                        defaultValue={row.descriptions}
                                        inputProps={{
                                        style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.descriptions}
                                        helperText={
                                        errors?.rows?.[globalIndex]?.descriptions?.message
                                        }
                                    />
                                </TableCell>
                                ) : (
                                <TableCell align="center">
                                    {row.descriptions || '-'}
                                </TableCell>
                                )}

                                {/* ยอดรวมทั้งหมด (ยอดชำระ) */}
                                {allowEdit ? (
                                    <TableCell>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.amount`)}
                                        defaultValue={row.amount}
                                        inputProps={{
                                            type: 'number',
                                            min: 1,
                                            style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.amount}
                                        helperText={
                                            errors?.rows?.[globalIndex]?.amount?.message
                                        }
                                        />
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                        {addCommas(row.amount, 2) || '-'}
                                    </TableCell>
                                )}

                                {/* อีเมลล์ */}
                                {allowEdit ? (
                                    <TableCell align="center">
                                        <TextareaAutosize
                                            minRows={1}
                                            defaultValue={row.email}
                                            {...register(`rows.${globalIndex}.email`)}
                                            aria-label={ariaLabel['aria-label']}
                                            style={{
                                            textAlign: 'left',
                                            width: '120px',
                                            resize: 'none',
                                            border: 'none',
                                            borderBottom: !!errors?.rows?.[globalIndex]?.email
                                                ? '1px solid red'
                                                : '1px solid #AFAFAF',
                                            paddingBottom: '4px',
                                            outline: 'none',
                                            background: 'none',
                                            }}
                                        />
                                        {!!errors?.rows?.[globalIndex]?.email && (
                                            <p
                                            style={{
                                                color: 'red',
                                                fontSize: '12px',
                                                marginTop: '4px',
                                            }}
                                            >
                                            {errors?.rows?.[globalIndex]?.email?.message}
                                            </p>
                                        )}
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                    {row.email || '-'}
                                    </TableCell>
                                )}
                                
                                {/* Action */}
                                <TableCell align="center">
                                  <IconButton
                                    onClick={() => remove(globalIndex)}
                                    aria-label="delete"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>

                            </TableRow>
                            );
                          })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* </Paper> */}

                <TablePagination
                  sx={{
                    width: 'auto',
                    marginRight: '8px',
                    '& .MuiTypography-caption': { display: 'none' },
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  rowsPerPage={rowsPerPage}
                  page={page}
                  count={total}
                  labelRowsPerPage="Rows"
                  onPageChange={(event, newPage) => {
                    setPage(newPage);
                  }}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
                <div className="mt-7 flex justify-end">
                  <div className="mr-3">
                    {showData && (
                      <Button type="submit" variant="contained">
                        <div className="text-white">ยืนยันอัปโหลดข้อมูล</div>
                      </Button>
                    )}
                  </div>
                  <div>
                    <Button onClick={cancel} variant="outlined" color="error">
                      ยกเลิการอัปโหลด
                    </Button>
                  </div>
                </div>
            </div>
        </form>
    </div>
  )
}

export default TableUploadDefault
